// @oasis-permission-inherits: /studio-edits (studio-edits:view)
import { Icon } from "@mirage-web/icons";
import { Link, createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";

import { Card, CardContent } from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import { aiEditsFeedSearchSchema } from "~/modules/ai-edits/schemas";
import { EditTypeBadge } from "~/modules/edits/components/edit-type-badge";
import { VideoTypeBadge } from "~/modules/edits/components/video-type-badge";
import { fetchStudioEditDetailQuery } from "~/modules/edits/queries";

export const Route = createFileRoute("/_dash/studio-edits/view/$videoId")({
  component: RouteComponent,
  validateSearch: aiEditsFeedSearchSchema,
});

function RouteComponent() {
  const { videoId } = Route.useParams();
  const search = Route.useSearch();
  const { data, isLoading, error } = fetchStudioEditDetailQuery.useQuery({
    pathParams: { videoId },
  });

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (error || !data) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">
              Failed to load video details. Please try again later.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const video = data.video;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Link
          to="/ai-edits/all"
          search={search}
          className="text-sm text-muted-foreground hover:underline"
        >
          ← Back to AI Edits
        </Link>
      </div>
      <div>
        <button
          className="text-xl font-bold cursor-pointer"
          onClick={() => {
            navigator.clipboard.writeText(videoId);
            toast.success("Video ID copied to clipboard");
          }}
        >
          {`Video ID: ${videoId}`}
        </button>
      </div>

      <div className="flex flex-row gap-6">
        <div className="lg:col-span-2 flex items-start">
          <div className="relative overflow-hidden rounded-lg bg-muted max-h-[600px] flex items-center justify-center">
            <video
              src={video.url}
              controls
              className="max-w-full max-h-[600px] object-contain"
              preload="metadata"
            />
          </div>
        </div>

        <Card className="h-full">
          <CardContent className="space-y-3 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1">
            <div className="col-span-2 grid grid-cols-subgrid">
              <p className="text-sm font-medium text-muted-foreground mb-2">Video ID</p>
              <code className="text-xs bg-muted px-2 py-1 rounded break-all w-fit h-fit">
                {video.id}
              </code>
            </div>

            <div className="col-span-2 grid grid-cols-subgrid">
              <p className="text-sm font-medium text-muted-foreground mb-2">User ID</p>
              <Link
                to={`/users/view/$userId`}
                params={{ userId: video.userId }}
                className="flex items-center gap-1"
              >
                <code className="text-xs bg-muted px-2 py-1 rounded break-all hover:underline w-fit h-fit">
                  {video.userId}
                </code>
                <Icon name="link-out" className="h-3 w-3" />
              </Link>
            </div>

            <div className="col-span-2 grid grid-cols-subgrid">
              <p className="text-sm font-medium text-muted-foreground mb-2">Org ID</p>
              <Link
                to={`/organizations/view/$orgId`}
                params={{ orgId: video.orgId }}
                className="flex items-center gap-1"
              >
                <code className="text-xs bg-muted px-2 py-1 rounded break-all hover:underline w-fit h-fit">
                  {video.orgId}
                </code>
                <Icon name="link-out" className="h-3 w-3" />
              </Link>
            </div>

            <div className="col-span-2 grid grid-cols-subgrid">
              <p className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-1">
                Created At
              </p>
              <div className="flex flex-col">
                <p className="text-sm">{formatDate(video.createdAt)}</p>
                <p className="text-xs text-muted-foreground">
                  {formatRelativeTime(video.createdAt)}
                </p>
              </div>
            </div>

            <div className="col-span-2 grid grid-cols-subgrid">
              <p className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-1">
                Updated At
              </p>
              <div className="flex flex-col">
                <p className="text-sm">{formatDate(video.updatedAt)}</p>
                <p className="text-xs text-muted-foreground">
                  {formatRelativeTime(video.updatedAt)}
                </p>
              </div>
            </div>

            <div className="col-span-2 grid grid-cols-subgrid">
              <p className="text-sm font-medium text-muted-foreground mb-2">Video Source</p>
              <VideoTypeBadge videoType={video.type} />
            </div>

            <div className="col-span-2 grid grid-cols-subgrid">
              <p className="text-sm font-medium text-muted-foreground mb-2">Edit Type</p>
              <EditTypeBadge editType={video.editType} />
            </div>

            <div className="col-span-2 grid grid-cols-subgrid">
              <p className="text-sm font-medium text-muted-foreground mb-2">Edit Name</p>
              <p className="text-sm">{video.editName}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Skeleton className="h-4 w-32" />
      </div>
      <div>
        <Skeleton className="h-6 w-48" />
      </div>

      <div className="flex flex-row gap-6 h-[600px]">
        <div className="lg:col-span-2 flex items-start">
          <div className="relative overflow-hidden rounded-lg bg-muted h-full w-[338px]">
            <Skeleton className="w-full h-full" />
          </div>
        </div>

        <div>
          <Card className="h-fit">
            <CardContent className="space-y-3 grid grid-cols-2 gap-x-4 gap-y-1">
              <div className="col-span-2 grid grid-cols-subgrid">
                <Skeleton className="h-6 w-16" />
                <Skeleton className="h-4 w-full" />
              </div>

              <div className="col-span-2 grid grid-cols-subgrid">
                <Skeleton className="h-6 w-20" />
                <Skeleton className="h-4 w-32" />
              </div>

              <div className="col-span-2 grid grid-cols-subgrid">
                <Skeleton className="h-6 w-20" />
                <Skeleton className="h-4 w-32" />
              </div>

              <div className="col-span-2 grid grid-cols-subgrid">
                <Skeleton className="h-6 h-6 w-20 mb-2" />
                <Skeleton className="h-4 w-20" />
              </div>

              <div className="col-span-2 grid grid-cols-subgrid">
                <Skeleton className="h-6 w-20 mb-2" />
                <Skeleton className="h-4 w-28" />
              </div>

              <div className="col-span-2 grid grid-cols-subgrid">
                <Skeleton className="h-6 w-24 mb-2" />
                <Skeleton className="h-4 w-24" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function formatDate(timestampMs: number): string {
  const date = new Date(timestampMs);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatRelativeTime(timestampMs: number): string {
  const seconds = Math.floor((Date.now() - timestampMs) / 1000);

  if (seconds < 60) {
    return "just now";
  }
  if (seconds < 3600) {
    return `${Math.floor(seconds / 60)} minutes ago`;
  }
  if (seconds < 86400) {
    return `${Math.floor(seconds / 3600)} hours ago`;
  }
  if (seconds < 604800) {
    return `${Math.floor(seconds / 86400)} days ago`;
  }
  if (seconds < 2592000) {
    return `${Math.floor(seconds / 604800)} weeks ago`;
  }
  if (seconds < 31536000) {
    return `${Math.floor(seconds / 2592000)} months ago`;
  }
  return `${Math.floor(seconds / 31536000)} years ago`;
}
